<template>
    <default-calculator-layout :filter-width="filterWidth" :output-width="outputWidth" :calcFilter="calcFilter" :calculator-title="calculatorTitle" class="trade-in-value">
        <div slot="filter">
            <ul class="estimate-your-payment">
                <li>
                    <drop-down
                        v-model="CreditScore"
                        :label="labelEstCreditScore"
                        :options="CreditScoreList"
                    />
                </li>
                <li class="two-box">
                    <number-input
                        v-model="PurchasePrice"
                        :label="labelPurchasePrice"
                        @input="calculatePayment()"
                    />
                    <number-input
                        v-model="TotalAPR"
                        :label="labelAPR"
                        symbol="%"
                        symbol-aligned="right"
                        precision="2"
                        maxlength="5"
                        :tooltip-link="aprTooltip"
                        @input="calculatePayment()"
                    />
                </li>
                <li>
                    <drop-down
                        v-model="LoanTermInYears"
                        :label="labelLoanTermInYears"
                        :options="LoanTermInYearsList"
                        @input="calculatePayment()"
                    />
                </li>
                <li>
                    <number-input
                        v-model="DownPayment"
                        :label="labelDownPayment"
                        :tooltip-link="downPaymentTooltip"
                        @input="calculatePayment()"
                    />
                </li>
                <li class="list-tradeinvalue">
                    <number-input
                        v-model="TradeInValue"
                        :label="labelTradeInValue"
                        :rightlabel="helpMeText"
                        :right-label-link="rightlabelLink"
                        showTooltip="true"
                        :show-question-mark="ShowQuestionMark"
                        :popper-content="PopperContent"
                        class="tooltip-icon"
                        :tooltip-link="tradeinValueTooltip"
                        @showPopup="showPopup"
                        @input="calculatePayment()"
                    />
                </li>
                <li>
                    <number-input
                        v-model="AmountOwedTradeIn"
                        :label="labelAmountOwedTradeIn"
                        :tooltip-link="amountOwedTooltip"
                        @input="calculatePayment()"
                    />
                </li>
            </ul>
        </div>
        <div slot="output" class="no-padding-xs">
            <div class="payment-tabs">
                <ul class="tab-name">
                    <li :class="{'active': TabPayment}" @click="updateTabValues('TabPayment')">
                        Price & Payments
                    </li>
                    <li :class="{'active': TabBreakdown}" @click="updateTabValues('TabBreakdown')">
                        Payment Breakdown
                    </li>
                </ul>
                <div v-show="TabPayment" class="tab-content-details" :class="{'active': TabPayment}">
                    <h1 class="monthly-payment">Monthly Payment: <span>${{ currencyFilter(MonthlyPayment, 0) }}</span></h1>
                    <div class="price-payment">
                        <div>
                            <h3><b>Loan Amount</b></h3>
                            <h3>${{ currencyFilter(LoanAmount, 0) }}</h3>
                        </div>
                        <div>
                            <h3><b>Total Paid</b></h3>
                            <h3>${{ currencyFilter(TotalPaid, 0) }}</h3>
                        </div>
                    </div>
                </div>
                <div v-show="TabBreakdown" class="tab-content-details" :class="{'active': TabBreakdown}">
                    <div class="payment-breakdown">
                        <div class="table-responsive">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>Year</th>
                                        <th>Interest Paid</th>
                                        <th>Principal Paid</th>
                                        <th>Ending Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(payment, index) in paymentBreakdown" v-if="payment != null" :key="index">
                                        <td>{{ index }}</td>
                                        <td>${{ currencyFilter(payment.interest, 2) }}</td>
                                        <td>${{ currencyFilter(payment.principal, 2) }}</td>
                                        <td>${{ currencyFilter(payment.balance, 2) }}</td>
                                    </tr>
                                    <tr>
                                        <td>Total paid</td>
                                        <td>${{ currencyFilter(TotalIntersetPaid, 2) }}</td>
                                        <td>${{ currencyFilter(TotalPrincipal, 2) }}</td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <div class="offer-button">
                <button class="btn btn-orange" @click="redirect()">{{ DestinationButtonText }}</button>
                <advertising-disclosures
                    v-if="DisclosureText"
                    :disclosure-text="disclosureText"
                    :disclosure-tokens="disclosureTokens"
                    class="disclosures"
                />
            </div>
            <help-me-calculate-popup
                v-if="ShowModal"
                :show-modal="ShowModal"
                :popup-title="PopupTitle"
                :year-list="YearList"
                :trim="Trim"
                :year="Year"
                :make="Make"
                :model="Model"
                :condition="Condition"
                :zip-code="ZipCode"
                :start-date="startDate"
                :end-date="endDate"
                :mileage="Mileage"
                @closeModal="showPopup($event)"
                @updateTradeIn="updateTradeIn($event)"
            />
        </div>
    </default-calculator-layout>
</template>

<script>
    import Accounting from 'accounting';
    import CreditScoreList from '~/constants/credit-score-list';
    import LoanTermList from '~/constants/loan-term-list';
    import DefaultTooltip from '~/constants/tooltip-defaults';
    import BaseMixin from '~/mixin/base-mixin';
    import helpMeCalculatePopup from './components/help-me-calculate-popup.vue';
    import { getYearList as getYearListSvc } from '~/services/tradeIn-service';
    import DefaultCalculatorLayout from '~/layout/default-calculator.vue';

    export default {
        props: {
            helpMeText: {
                default: 'Help me calculate',
            },
            labelEstCreditScore: {
                default: 'Credit Score:',
            },
            labelPurchasePrice: {
                default: 'Purchase Price:',
            },
            labelAPR: {
                default: 'APR:',
            },
            labelLoanTermInYears: {
                default: 'Loan Term:',
            },
            loanTermInYearsListType: {
                default: 'oemfinancecalc',
            },
            labelDownPayment: {
                default: 'Down Payment:',
            },
            labelTradeInValue: {
                default: 'Trade-In Value:',
            },
            labelAmountOwedTradeIn: {
                default: 'Amount Owed on Trade-In:',
            },
            calculateButtonText: {
                default: 'Calculate',
            },
            loanTermInYears: {
                default: 5,
            },
            creditScore: {
                default: '2',
            },
            purchasePrice: {
                default: 25000,
            },
            totalAPR: {
                default: '3.99',
            },
            downPayment: {
                default: 0,
            },
            tradeInValue: {
                default: 0,
            },
            amountOwedTradeIn: {
                default: 0,
            },
            interestRateTooltip: {
                default: DefaultTooltip.InterestRate,
            },
            mortgagePaymentSection: {
                default: 1,
            },
            popupTitle: {
                default: 'Calculate your trade-in value',
            },
            calculatorTitle: {
                default: 'Estimate Your Payment',
            },
            rightlabelLink: {
                default: '',
            },
            downPaymentTooltip: {
                default: DefaultTooltip.CarDownPayment,
            },
            aprTooltip: {
                default: DefaultTooltip.CarAnnualPercentageRate,
            },
            amountOwedTooltip: {
                default: DefaultTooltip.AmountOwedOnTrade,
            },
            destinationButtonText: {
                default: 'Find Your 5 Best Car Loan Offers',
            },
            destinationLinkOrId: {
                default: 'wp-auto',
            },
            showQuestionMark: {
                default: true,
            },
            popperContent: {
                default: '',
            },
            year: {
                default: new Date().getFullYear(),
            },
            make: {
                default: 0,
            },
            model: {
                default: 0,
            },
            trim: {
                default: 0,
            },
            condition: {
                default: 'good',
            },
            zipCode: {
                default: 28277,
            },
            startDate: {
                default: '',
            },
            endDate: {
                default: '',
            },
            tradeinValueTooltip: {
                default: DefaultTooltip.TradeInValue,
            },
            disclosureTokens: {
                default: '00001,00057,00058,00059,00095,00096',
            },
            disclosureText: {
                default: 'Disclosures',
            },
        },
        data() {
            return {
                AmountOwedTradeIn: this.amountOwedTradeIn,
                DownPayment: this.downPayment,
                TradeInValue: this.tradeInValue,
                TotalAPR: this.totalAPR,
                PurchasePrice: this.purchasePrice,
                LoanTermInYears: this.loanTermInYears,
                LoanTermInYearsList: LoanTermList.LoanTermInYearsList[this.loanTermInYearsListType],
                CreditScoreList: CreditScoreList.oemfinancecrditscore,
                CreditScore: this.creditScore,
                CalculateButtonText: this.calculateButtonText,
                InterestRateTooltip: parseInt(this.hideTooltip, 10) ? '' : this.interestRateTooltip,
                PaymentFrequency: 'monthly',
                TabPayment: true,
                TabBreakdown: false,
                PopupTitle: this.popupTitle,
                LoanAmount: 0,
                TotalPaid: 0,
                MonthlyPayment: 0,
                paymentBreakdown: [],
                TotalIntersetPaid: 0,
                TotalPrincipal: 0,
                DestinationButtonText: this.destinationButtonText,
                DestinationLinkOrId: this.destinationLinkOrId,
                ShowModal: false,
                ShowQuestionMark: this.showQuestionMark,
                PopperContent: this.popperContent,
                Year: 0,
                Make: 0,
                Model: 0,
                YearList: [],
                Trim: 0,
                Condition: this.condition,
                ZipCode: this.zipCode,
                TradeinValueTooltip: this.tradeinValueTooltip,
                Mileage: 0,
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,

            };
        },
        methods: {
            showPopup(data) {
                this.ShowModal = data;
                document.body.style.WebkitOverflowScrolling = 'touch';
                document.body.style.position = 'relative';
            },
            getPaymentFrequency(val) {
                this.PaymentFrequency = val;
            },
            updateTabValues(Value) {
                this.TabPayment = Value === 'TabPayment';
                this.TabBreakdown = Value === 'TabBreakdown';
            },
            calculatePayment() {
                this.TotalPaid = 0;
                this.TotalIntersetPaid = 0;
                this.TotalPrincipal = 0;
                this.LoanAmount = this.unformatFigure(this.PurchasePrice) - this.unformatFigure(this.DownPayment) - (this.unformatFigure(this.TradeInValue) - this.unformatFigure(this.AmountOwedTradeIn));
                const monthlyApr = this.unformatFigure(this.TotalAPR) / 1200;
                const loanTeamInMonths = this.LoanTermInYears * 12;
                const paymentBreakdown = [];
                let balance = this.LoanAmount;
                let interest = 0;
                let principal = 0;
                this.MonthlyPayment = monthlyApr * this.LoanAmount / (1 - (1 + monthlyApr) ** -loanTeamInMonths);
                for (let year = 1; year <= this.LoanTermInYears; year++) {
                    paymentBreakdown[year] = {};
                    for (let month = 1; month <= 12; month++) {
                        if (paymentBreakdown[year].interest === undefined) paymentBreakdown[year].interest = 0;
                        if (paymentBreakdown[year].principal === undefined) paymentBreakdown[year].principal = 0;
                        if (paymentBreakdown[year].balance === undefined) paymentBreakdown[year].balance = 0;
                        interest = monthlyApr * balance;
                        principal = this.MonthlyPayment - interest;
                        balance -= principal;
                        this.TotalIntersetPaid += interest;
                        this.TotalPrincipal += principal;
                        paymentBreakdown[year].interest += interest;
                        paymentBreakdown[year].principal += principal;
                        paymentBreakdown[year].balance = balance;
                    }
                }
                this.TotalPaid = this.TotalIntersetPaid + this.TotalPrincipal;
                this.paymentBreakdown = paymentBreakdown;
            },
            currencyFilter(figure, precision) {
                return figure > 0 ? Accounting.formatNumber(figure, { precision }) : 0;
            },
            fetchYearList() {
                getYearListSvc('used').then((data) => {
                    const yearList = [];
                    if (data) {
                        data.forEach((year) => {
                            yearList.push({
                                text: year,
                                value: year,
                            });
                        });
                    }
                    this.YearList = yearList;
                });
            },
            updateTradeIn(data) {
                document.body.style.WebkitOverflowScrolling = 'touch';
                document.body.style.position = 'relative';
                if (data) {
                    this.TradeInValue = data.condition;
                    this.Year = data.year;
                    this.Make = data.make;
                    this.Model = data.model;
                    this.Trim = data.trim;
                    this.Mileage = data.mileage;
                    this.ZipCode = data.zipcode;
                    this.Condition = data.ConditionVal;
                }
                this.ShowModal = false;
                this.calculatePayment();
            },
        },
        mounted() {
            this.calculatePayment();
            this.showPopup(false);
            this.PopperContent = '<div class="popup-content large-content"><p>The Kelley Blue Book&#174; Trade-in Value is Kelley Blue Book&apos;s estimate of what a consumer can expect to receive from a dealer for a trade-in vehicle assuming an accurate appraisal of condition. This value will likely be less than the Kelley Blue Book&#174; Private Party Value because the reselling dealer incurs the cost of safety inspections, reconditioning and other costs of doing business.</p><p>This value is based upon standard vehicle options and equipment.</p></div>';
            this.fetchYearList();
        },
        components: {
            helpMeCalculatePopup,
            DefaultCalculatorLayout,
        },
        mixins: [BaseMixin],
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
